'use client';

import {useMemo} from 'react';
import {useAuth as useOidcAuth} from 'react-oidc-context';
import {type AuthState, type AuthUser, AuthMode} from '@/matic/types/auth';
import {type UserRole} from '@/matic/types/roles';

/**
 * Get authentication mode from environment
 *
 * Uses VITE_AUTH_MODE to determine auth strategy:
 * - 'cognito': Real AWS Cognito authentication (default)
 * - 'local-jwt': Email + password against the identity service (no AWS)
 * - 'local': Mock authentication for quick local dev without AWS
 */
function getAuthMode(): AuthMode {
  const authMode = (
    import.meta.env.VITE_AUTH_MODE as string | undefined
  )?.toLowerCase();

  // Default to COGNITO for production safety. 'local-jwt' (password login against
  // the identity service) rides the same OIDC code path: lib/auth/user-manager.ts
  // hands react-oidc-context a LocalUserManager whose tokens carry the same claims.
  if (!authMode || authMode === 'cognito' || authMode === 'local-jwt') {
    return AuthMode.COGNITO;
  }

  if (authMode === 'local') {
    // Log warning in development to make LOCAL mode obvious
    if (import.meta.env.DEV) {
      console.warn(
        '⚠️  Using LOCAL auth mode with mock user. Set VITE_AUTH_MODE=cognito for real authentication.'
      );
    }

    return AuthMode.LOCAL;
  }

  // Invalid value - default to COGNITO for safety
  console.error(
    `Invalid VITE_AUTH_MODE: "${authMode}". Defaulting to COGNITO.`
  );
  return AuthMode.COGNITO;
}

/**
 * Decode JWT payload to extract custom Cognito claims
 */
function decodeJwtPayload(token: string): Record<string, any> {
  try {
    const base64Url = token.split('.')[1];
    const bytes = Uint8Array.from(
      globalThis.atob(base64Url.replaceAll('-', '+').replaceAll('_', '/')),
      (c) => c.codePointAt(0)!
    );
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, any>;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return {};
  }
}

/**
 * Check if a JWT token is expired
 * JWT exp claim is seconds since epoch
 */
function isTokenExpired(tokenPayload: Record<string, any>): boolean {
  const {exp} = tokenPayload;
  if (!exp || typeof exp !== 'number') {
    return true; // Treat missing/invalid exp as expired
  }

  // Compare exp (seconds) with current time (milliseconds)
  const expiryTime = exp * 1000;
  const now = Date.now();

  // Add 5 second buffer to account for clock skew and network delays
  return now > expiryTime + 5000;
}

/**
 * Transform OIDC ID token into AuthUser
 */
function transformOidcUser(
  tokenPayload: Record<string, any>
): AuthUser | undefined {
  const userId = tokenPayload['custom:user_id'];
  const tenantId = tokenPayload['custom:tenant_id'];
  const role = tokenPayload['custom:role'];

  if (!userId || !tenantId || !role) {
    console.error('Missing required custom attributes in ID token:', {
      userId: Boolean(userId),
      tenantId: Boolean(tenantId),
      role: Boolean(role),
    });
    return undefined;
  }

  return {
    id: userId,
    email: tokenPayload.email ?? '',
    name: tokenPayload.name ?? tokenPayload.email ?? '',
    image: tokenPayload.picture,
    tenantId,
    role: role as UserRole,
  };
}

/**
 * Enhanced authentication hook using react-oidc-context
 * Uses react-oidc-context with Cognito as the OIDC provider
 */
export function useAuth(): AuthState {
  const authMode = useMemo(() => getAuthMode(), []);
  const oidcAuth = useOidcAuth();

  // Handle local development mode
  if (authMode === AuthMode.LOCAL) {
    const localUser: AuthUser = {
      id: 'local-user',
      email: 'local@matic.com',
      name: 'Local User',
      tenantId: 'test-tenant-id',
      role: 'admin',
    };
    return {
      isLoading: false,
      isAuthenticated: true,
      user: localUser,
      session: {user: localUser},
    };
  }

  // OIDC/Cognito mode
  const {isLoading, isAuthenticated, user: oidcUser} = oidcAuth;

  // Extract and transform user from ID token
  const user = useMemo(() => {
    if (!isAuthenticated || !oidcUser?.id_token) {
      return undefined;
    }

    const tokenPayload = decodeJwtPayload(oidcUser.id_token);

    // Check if token is expired - if so, user is not authenticated
    if (isTokenExpired(tokenPayload)) {
      console.warn('⚠️  ID token has expired');
      return undefined;
    }

    return transformOidcUser(tokenPayload);
  }, [isAuthenticated, oidcUser?.id_token]);

  return {
    isLoading,
    isAuthenticated: isAuthenticated && Boolean(user),
    user,
    session: user ? {user} : undefined,
  };
}

/**
 * Hook to get the current authentication mode
 */
export function useAuthMode(): AuthMode {
  return useMemo(() => getAuthMode(), []);
}

/**
 * Hook to check if the current environment is in local development mode
 */
export function useIsLocalDev(): boolean {
  const authMode = useAuthMode();
  return authMode === AuthMode.LOCAL;
}
